import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation } from 'next-i18next'
import { useState, useContext, useEffect, useMemo } from 'react'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'
import { NextPage } from 'next'

import { Page } from '../../components/Page'
import { Section } from '../../components/Section'
import { H3, P } from '../../components/Headings'
import { Event } from '../../components/Event'
import { CTA } from '../../components/CTA'
import { useEvent } from '../../hooks/event'
import { Hyperlink } from '../../components/Hyperlink'
import { UserContext, ToastContext, AnalyticsContext } from '../../context'
import {
  useRedirectIfNotAuthenticated,
  useUserRedirectIfEventNotOwned,
} from '../../hooks/redirect'
import { colors } from '../../utils'
import { ZoomAuthoriseModal } from '../../components/ZoomAuthoriseModal'
import { GroupModal } from '../../components/GroupModal'
import { TimezoneUpdateModal } from '../../components/TimezoneUpdateModal'
import { DiscardModal } from '../../components/Event/DiscardModal'
import { CloseIcon } from '../../components/CloseIcon'
import { GroupModalType, ToastType } from '../../types'
import { useAnalyticsEvent } from '../../hooks/analyticsEvent'

// Styles
import { event_createPageStyles } from '../../components/pageStyles/event_createPageStyles'
import { useOrigin } from '../../hooks'

export const PageContainer = styled(Page)`
  ${event_createPageStyles}
`

// Defs
interface Props {
  hostGroupId?: number
  origin: string
}

// Dependencies.
const EventPage: NextPage<Props> = (props) => {
  const { t } = useTranslation('common')
  const origin = useOrigin()

  // Redirect If Not Authenticated.
  useRedirectIfNotAuthenticated('/login?redirect=/events/create')

  // Global & Shared State
  const router = useRouter()
  const hostGroupId = parseInt(router.query.hostgroup as string)

  const analyticEvent = useAnalyticsEvent()
  const toastContext = useContext(ToastContext)
  const { currentUser, fetchCurrentUser } = useContext(UserContext)
  const duplicateEventHook = useEvent(parseInt(router.query?.eventId as string))
  const newEventHook = useEvent()
  const { trackEventPublished } = useContext(AnalyticsContext)
  // Calc
  const pageIsCreate = router.pathname === '/events/create' ? true : false
  const eventHook = pageIsCreate === true ? newEventHook : duplicateEventHook
  const {
    value,
    setIsEditable,
    getDraftNookEvent,
    updateFieldsWithPartial,
    createE,
    showZoomAuthoriseModal,
    updateShowZoomAuthoriseModal,
    showDropDownCreateGroupModal,
    showDropDownCreateStripeModal,
    updateShowDropDownCreateGroupModal,
    updateShowDropDownCreateStripeModal,
  } = eventHook

  // State
  const [loading, setLoading] = useState(false)
  // const [showGroupModal, setShowGroupModal] = useState(false) // No longer in use -> see comments below
  const [userDismissZoomModal, setUserDismissZoomModal] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [showTimezoneUpdateModal, setShowTimezoneUpdateModal] = useState(false)
  const [showDiscardModal, setShowDiscardModal] = useState(false)

  // Redirect If the user dose not own the event
  useUserRedirectIfEventNotOwned(value, currentUser, '/403')
  // Effects
  // On mount
  useEffect(() => {
    // make a new event or get event to duplicate
    ;(async () => {
      const eventId: number = parseInt(
        (router.query?.eventId || undefined) as string,
      )
      // Make sure it is a number
      if (
        `${eventId}` === router.query?.eventId &&
        !isNaN(eventId) &&
        eventId !== undefined
      ) {
        await duplicateEventHook.fetch()
        await duplicateEventHook.makeDuplicate(eventId)
        setIsEditable(true)
        toastContext.addToast({
          id: 'duplicated event',
          toastType: ToastType.Success,
          dismissTime: 8000,
          title: t(
            'Duplicated draft event created',
            'Duplicated draft event created',
          ),
          content: t(
            'A new draft event has been created with all the details of the chosen last event. Ensure to apply a new time and date.',
            'A new draft event has been created with all the details of the chosen last event. Ensure to apply a new time and date.',
          ),
        })
      } else {
        getDraftNookEvent()
        setIsEditable(true)
      }
    })()
  }, [])

  const [getUserManagedGroupId, setGetUserManagedGroupId] = useState(null)

  // IDK - Do some stuff for groups
  useEffect(() => {
    if (
      currentUser &&
      currentUser.managed_groups &&
      currentUser.managed_groups.length > 0
    ) {
      const managedGroup = currentUser.managed_groups[0]
      if (managedGroup) {
        setGetUserManagedGroupId(managedGroup.id)
      }
    }
  }, [currentUser])

  const setGroupId = (id) => {
    if (typeof id === 'number') {
      setGetUserManagedGroupId(id)
    }
  }

  // As the user loads in set their time zone
  useEffect(() => {
    if (currentUser) {
      if (!currentUser.time_zone || currentUser.time_zone === '') {
        setShowTimezoneUpdateModal(true)
      }
      // trigger the first time - create group modal - no longer in use
      // if (!currentUser.has_created_first_event && currentUser.managed_groups.length === 0) {
      //   setShowGroupModal(true)
      // }
    }
  }, [currentUser])

  // IDK - something to do with the modal
  useEffect(() => {
    // console.log('effect run')
    if (userDismissZoomModal) {
      // console.log('if dismiss tru')
      if (value.video_host_type === 0) {
        setUserDismissZoomModal(false)
        // console.log('set dismis false')
        if (currentUser && currentUser.has_zoom_access_token === false) {
          updateShowZoomAuthoriseModal(true)
          // console.log('auth show')
        }
      }
    } else {
      setUserDismissZoomModal(true)
    }
  }, [value.video_host_type])

  // Funcs
  const onSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      // console.log('start onsubmit')
      setErrorMessage('')
      // Prevent Default.
      e.preventDefault()

      if (!eventHook.validateEvent()) {
        // console.log('error of sorts')
        return
      }

      // Flag Loading.
      setLoading(true)

      // console.log('after loading')
      // Check user object again for zoom access token
      if (value.video_host_type === 0) {
        if (currentUser.has_zoom_access_token === false) {
          const user = await fetchCurrentUser()
          if (user.has_zoom_access_token === false) {
            updateShowZoomAuthoriseModal(true)
            setLoading(false)
            return
          }
        }
      }

      await createE()
        .then(async (res) => {
          analyticEvent.fbSubmitApplication()
          trackEventPublished({
            eventTitle: eventHook?.value?.title,
            eventId: res?.toString() || undefined,
          })
          router.replace(`/events/manage/${res}?success=true`)
        })
        .catch((error) => {
          console.log(error)
        })
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
    }
  }

  // console.log('@Create->eventHock',eventHook)
  // ..
  return (
    <PageContainer>
      {/* Title Section. */}
      <Section>
        {/* Title. */}
        <H3 className="titleSection">
          {t(`create462213449`, `Create an event`)}
        </H3>
      </Section>

      {/* Event. */}
      <Event
        eventHook={eventHook}
        hostGroupId={hostGroupId}
        groupId={getUserManagedGroupId}
        setGroupId={setGroupId}
      />

      {/* Buttons. */}
      <Section className="buttonsSection">
        {/* Create Button. */}
        <div className="saveButtonSection">
          <CTA
            className="purple saveButton"
            onClick={onSubmit}
            disabled={loading}>
            {t(`Publish event`, `Publish event`)}
          </CTA>
          {(eventHook.errorMessage !== '' || errorMessage !== '') && (
            <div className="errorContainer">
              <P className="detail" style={{ color: `${colors.red}` }}>
                {eventHook.errorMessage || errorMessage}
              </P>
            </div>
          )}
        </div>

        {/* Cancel Button. */}
        <Hyperlink className="cancelButton" href="/">
          <CTA>{t(`Cancel`, `Cancel`)}</CTA>
        </Hyperlink>
      </Section>
      {/* First Time - Ask user to create a group - no longer in use */}
      {/* {showGroupModal && <GroupModal
      close={() => (setShowGroupModal(false))}
      modalType={GroupModalType.NormalGroup}
      groupId={0}
    />} */}

      {showDropDownCreateGroupModal && (
        <GroupModal
          close={() => updateShowDropDownCreateGroupModal(false)}
          modalType={GroupModalType.DropDownGroup}
          groupId={0}
          eventHook={eventHook}
        />
      )}

      {showDropDownCreateStripeModal && (
        <GroupModal
          close={() => updateShowDropDownCreateStripeModal(false)}
          modalType={GroupModalType.DropDownStripe}
          groupId={getUserManagedGroupId}
          origin={origin}
          eventHook={eventHook}
        />
      )}

      {showZoomAuthoriseModal && (
        <ZoomAuthoriseModal
          eventHook={eventHook}
          userId={currentUser.id}
          close={(clickYes: boolean) => {
            updateShowZoomAuthoriseModal(false)
            setUserDismissZoomModal(true)
            //Auto select "Manual link" if user didn't select authorize zoom
            if (!clickYes) updateFieldsWithPartial({ video_host_type: 1 })
          }}
        />
      )}

      {/* update timezone modal*/}
      {showTimezoneUpdateModal && (
        <TimezoneUpdateModal
          header={t(
            `create986288651`,
            `We’re missing a lil’ something about you`,
          )}
          content={t(
            `create1570320882`,
            `It looks like you never set your time zone location. This helps us communicate to everyone the correct time in relation to where they are in the world.`,
          )}
          close={() => {
            setShowTimezoneUpdateModal(false)
          }}
        />
      )}

      <button onClick={() => setShowDiscardModal(true)} className="exitButton">
        <CloseIcon />
      </button>

      {showDiscardModal && (
        <DiscardModal close={() => setShowDiscardModal(false)} />
      )}
    </PageContainer>
  )
}

// Export.
export default EventPage
export async function getStaticProps({ locale }) {
  const translations = await serverSideTranslations(locale, ['common'])
  return {
    props: {
      ...translations,
    },
  }
}
